import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Stack,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import FormProvider, { RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { TopUpWithdrawTxType } from 'src/type/topup-withdraw';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import { toastError } from 'src/utils/format-error';
import { fData, fNumber } from 'src/utils/format-number';
import * as Yup from 'yup';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import dayjs, { Dayjs } from 'dayjs';
import axios, { endpoints } from 'src/utils/axios';
import InfoGrid from 'src/components/ui-kit/info-grid';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const Schema = Yup.object().shape({
  paidAtDate: Yup.mixed<Dayjs>().required('กรุณากรอกวันที่โอน'),
  paidAtTime: Yup.mixed<Dayjs>().required('กรุณากรอกเวลาที่โอน'),
  paymentImage: Yup.mixed<File>().required('กรุณาอัพโหลดรูปหลักฐาน'),
  reason: Yup.string(),
});

const defaultValues = {
  reason: '',
  paidAtDate: dayjs(),
  paidAtTime: dayjs(),
  paymentImage: null as unknown as File,
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  topUpWithdrawTx: TopUpWithdrawTxType;
  handleReload: VoidFunction;
};

export default function WithdrawApproveDialog({
  open,
  onClose,
  topUpWithdrawTx,
  handleReload,
}: Props) {
  const currentDateTime = useCurrentDateTime();

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (topUpWithdrawTx.status !== TransactionStatus.PENDING) {
        throw new Error('ไม่สามารถอัพเดทข้อมูลได้');
      }

      if (topUpWithdrawTx.type !== TransactionType.WITHDRAW_CREDIT) {
        throw new Error('ไม่สามารถอัพเดทข้อมูลได้');
      }

      if (!values.paymentImage) {
        throw new Error('กรุณาอัพโหลดรูปหลักฐาน');
      }

      const paymentImageUrl = await uploadImage(values.paymentImage, 'images');

      values.paidAtDate
        .set('hour', values.paidAtTime.hour())
        .set('minute', values.paidAtTime.minute());

      const body = {
        note: values.reason,
        paidAt: values.paidAtDate.toISOString(),
        paymentImageUrl,
        currentDateTime,
      };

      await axios.post(endpoints.transactions.approveWithdraw(topUpWithdrawTx.id), body);
      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('paymentImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const getInfo = () => {
    if (!topUpWithdrawTx) return [];

    const { user, creditAmount } = topUpWithdrawTx;

    return [
      { label: 'ผู้ใช้งาน', value: `${user?.firstName} ${user?.lastName}` },
      {
        label: 'ยอดที่ต้องการถอน',
        value: `${fNumber(creditAmount || 0)} THB`,
      },
      {
        label: 'ชื่อธนาคาร (ลูกค้า)',
        value: user?.userWallet?.bank?.name || '-',
      },
      {
        label: 'ชื่อบัญชี (ลูกค้า)',
        value: user?.userWallet?.accountName || '-',
      },
      {
        label: 'เลขที่บัญชี (ลูกค้า)',
        value: user?.userWallet?.accountNumber || '-',
      },
    ];
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>อนุมัติรายการถอน</DialogTitle>

        <DialogContent>
          <InfoGrid infos={getInfo()} />

          <Stack spacing={3}>
            <RHFUploadBox
              name="paymentImage"
              maxSize={3145728}
              onDrop={handleDropImage}
              placeholder="อัพโหลดรูปหลักฐาน"
              sx={{
                maxHeight: 140,
                width: '100%',
                height: '100%',
                aspectRatio: '1/1',
                mx: 'auto',
              }}
              helperText={
                <>
                  อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                  <br /> ขนาดสูงสุด {fData(3145728)}
                </>
              }
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Stack>
                  <Controller
                    name="paidAtDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={getValues('paidAtDate')}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                        }}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="paidAtTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      ampm={false}
                      format="HH:mm"
                      value={getValues('paidAtTime')}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <RHFTextField
              multiline
              rows={3}
              name="reason"
              placeholder="หมายเหตุ"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            ดำเนินการต่อ
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
